import { useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Booking, BookingStatus, PaymentStatus } from '../../types';
import ReservationAdminCard from './ReservationAdminCard';
import type { ReservationAction } from './ReservationQuickActions';
import {
  getReservationSearchHaystack,
  resolveBookingStatus,
  resolvePaymentStatus,
} from './reservationsAdmin.utils';

type Props = {
  bookings: Booking[];
  loading?: boolean;
  error?: string | null;
  busyId?: string | null;
  canValidateManualPayment: (booking: Booking) => boolean;
  canCancelBookingParticipants: (booking: Booking) => boolean;
  canCancelBooking: (booking: Booking) => boolean;
  canMarkAttendance: (booking: Booking) => boolean;
  canProcessRefund: (booking: Booking) => boolean;
  canProcessPartialRefund: (booking: Booking) => boolean;
  onValidateManualPayment: (booking: Booking) => void;
  onCancelBookingParticipants: (booking: Booking) => void;
  onCancelBooking: (booking: Booking) => void;
  onMarkAttendance: (booking: Booking, attendanceStatus: 'attended' | 'no_show') => void;
  onProcessRefund: (booking: Booking, action: 'refund' | 'refuse') => void;
  onProcessPartialRefund: (booking: Booking) => void;
};

export default function ReservationsAdminList({
  bookings,
  loading = false,
  error = null,
  busyId = null,
  canValidateManualPayment,
  canCancelBookingParticipants,
  canCancelBooking,
  canMarkAttendance,
  canProcessRefund,
  canProcessPartialRefund,
  onValidateManualPayment,
  onCancelBookingParticipants,
  onCancelBooking,
  onMarkAttendance,
  onProcessRefund,
  onProcessPartialRefund,
}: Props) {
  const [search, setSearch] = useState('');
  const [bookingStatusFilter, setBookingStatusFilter] = useState<'all' | BookingStatus>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<'all' | PaymentStatus>('all');
  const [actionFilter, setActionFilter] = useState<'all' | 'actionable'>('all');

  // The page container keeps the business rules; this list owns the admin-facing layout and client filters.
  const buildActions = (booking: Booking): ReservationAction[] => {
    const actions: ReservationAction[] = [];

    if (canValidateManualPayment(booking)) {
      actions.push({
        key: 'validate-payment',
        label: 'Valider virement',
        color: 'success',
        variant: 'contained',
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        onClick: () => onValidateManualPayment(booking),
      });
    }

    if (canMarkAttendance(booking)) {
      actions.push({
        key: 'mark-attended',
        label: 'Marquer présent',
        color: 'primary',
        variant: 'contained',
        icon: <PersonOutlineIcon fontSize="small" />,
        onClick: () => onMarkAttendance(booking, 'attended'),
      });
      actions.push({
        key: 'mark-no-show',
        label: 'Marquer no-show',
        color: 'error',
        variant: 'outlined',
        icon: <CloseRoundedIcon fontSize="small" />,
        onClick: () => onMarkAttendance(booking, 'no_show'),
      });
    }

    if (canProcessRefund(booking)) {
      actions.push({
        key: 'refund',
        label: 'Traiter remboursement',
        color: 'info',
        variant: 'contained',
        icon: <CreditCardOutlinedIcon fontSize="small" />,
        onClick: () => onProcessRefund(booking, 'refund'),
      });
      actions.push({
        key: 'refuse-refund',
        label: 'Refuser remboursement',
        color: 'inherit',
        variant: 'outlined',
        icon: <CloseRoundedIcon fontSize="small" />,
        onClick: () => onProcessRefund(booking, 'refuse'),
      });
    }

    if (canProcessPartialRefund(booking)) {
      actions.push({
        key: 'refund-partial',
        label: 'Traiter remboursement partiel',
        color: 'info',
        variant: 'contained',
        icon: <CreditCardOutlinedIcon fontSize="small" />,
        onClick: () => onProcessPartialRefund(booking),
      });
    }

    if (canCancelBookingParticipants(booking)) {
      actions.push({
        key: 'cancel-booking-partial',
        label: 'Annuler participants',
        color: 'warning',
        variant: 'outlined',
        icon: <CloseRoundedIcon fontSize="small" />,
        onClick: () => onCancelBookingParticipants(booking),
      });
    }

    if (canCancelBooking(booking)) {
      actions.push({
        key: 'cancel-booking',
        label: 'Annuler total',
        color: 'error',
        variant: 'outlined',
        icon: <CloseRoundedIcon fontSize="small" />,
        onClick: () => onCancelBooking(booking),
      });
    }

    return actions;
  };

  const filteredBookings = useMemo(() => {
    const normalizedQuery = search.trim().toLowerCase();

    return bookings.filter((booking) => {
      const bookingStatus = resolveBookingStatus(booking);
      const paymentStatus = resolvePaymentStatus(booking);
      const isActionable = buildActions(booking).length > 0;

      const matchesSearch = !normalizedQuery || getReservationSearchHaystack(booking).includes(normalizedQuery);
      const matchesBookingStatus = bookingStatusFilter === 'all' || bookingStatus === bookingStatusFilter;
      const matchesPaymentStatus = paymentStatusFilter === 'all' || paymentStatus === paymentStatusFilter;
      const matchesActionFilter = actionFilter === 'all' || isActionable;

      return matchesSearch && matchesBookingStatus && matchesPaymentStatus && matchesActionFilter;
    });
  }, [actionFilter, bookingStatusFilter, bookings, paymentStatusFilter, search]);

  const hasActiveFilters = Boolean(search) || bookingStatusFilter !== 'all' || paymentStatusFilter !== 'all' || actionFilter !== 'all';

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Liste des explorateurs
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {filteredBookings.length} résultat(s) affiché(s) sur {bookings.length} réservation(s)
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Nom, email, réf, communication"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: { md: 180 } }}>
            <Select
              value={bookingStatusFilter}
              onChange={(event) => setBookingStatusFilter(event.target.value as 'all' | BookingStatus)}
              displayEmpty
            >
              <MenuItem value="all">Toutes réservations</MenuItem>
              <MenuItem value="pending_payment">Attente paiement</MenuItem>
              <MenuItem value="pending_validation">À valider</MenuItem>
              <MenuItem value="confirmed">Confirmées</MenuItem>
              <MenuItem value="cancelled">Annulées</MenuItem>
              <MenuItem value="expired">Expirées</MenuItem>
              <MenuItem value="failed">Échec</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { md: 160 } }}>
            <Select
              value={paymentStatusFilter}
              onChange={(event) => setPaymentStatusFilter(event.target.value as 'all' | PaymentStatus)}
              displayEmpty
            >
              <MenuItem value="all">Tous paiements</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="paid">Payés</MenuItem>
              <MenuItem value="expired">Expirés</MenuItem>
              <MenuItem value="failed">Échoués</MenuItem>
              <MenuItem value="refunded">Remboursés</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { md: 160 } }}>
            <Select
              value={actionFilter}
              onChange={(event) => setActionFilter(event.target.value as 'all' | 'actionable')}
              displayEmpty
            >
              <MenuItem value="all">Toutes actions</MenuItem>
              <MenuItem value="actionable">Actions disponibles</MenuItem>
            </Select>
          </FormControl>

          {hasActiveFilters ? (
            <Button
              variant="text"
              onClick={() => {
                setSearch('');
                setBookingStatusFilter('all');
                setPaymentStatusFilter('all');
                setActionFilter('all');
              }}
              sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
            >
              Réinitialiser
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Stack spacing={1.5}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Paper key={index} elevation={0} sx={{ p: 2, borderRadius: 3 }}>
              <Skeleton variant="text" width="35%" height={28} />
              <Skeleton variant="text" width="50%" />
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Skeleton variant="rounded" width={100} height={28} />
                <Skeleton variant="rounded" width={120} height={28} />
                <Skeleton variant="rounded" width={140} height={28} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : bookings.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Aucune réservation pour le moment
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            La liste est prête pour accueillir les prochains dossiers.
          </Typography>
        </Paper>
      ) : filteredBookings.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Aucun résultat pour les filtres actuels
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Ajustez la recherche ou réinitialisez les filtres.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {/* Cards replace the wide table so the admin can process bookings without horizontal scrolling. */}
          {filteredBookings.map((booking) => (
            <ReservationAdminCard
              key={booking.id}
              booking={booking}
              actions={buildActions(booking)}
              isBusy={busyId === booking.id}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
